import { useEffect, useState } from "react";
import { api, apiError } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Plus, ShieldCheck } from "lucide-react";

const ROLES = {
  admin: "Administrator · Vollzugriff",
  mitarbeiter: "Mitarbeiter · Ankauf & Verkauf",
  lager: "Lager · Artikel & Bestand",
  buchhaltung: "Buchhaltung · Finanzen & Rechnungen",
  lesemodus: "Lesemodus · Nur lesen",
};

export default function Mitarbeiter() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", password: "", role: "mitarbeiter" });

  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.post("/users", form);
      toast.success("Mitarbeiter angelegt.");
      setOpen(false); setForm({ first_name: "", last_name: "", email: "", password: "", role: "mitarbeiter" }); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const toggleActive = async (u) => {
    await api.put(`/users/${u.id}`, { active: !u.active });
    load();
  };

  return (
    <div className="space-y-6" data-testid="mitarbeiter-page">
      <div className="flex justify-between items-end flex-wrap gap-3">
        <div>
          <div className="text-xs font-mono uppercase tracking-wider text-zinc-400">Team</div>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight mt-1">Mitarbeiter & Rollen</h1>
        </div>
        <Button onClick={() => setOpen(true)} className="bg-zinc-900 hover:bg-zinc-800 text-white" data-testid="new-employee-button"><Plus className="w-4 h-4 mr-1" /> Mitarbeiter hinzufügen</Button>
      </div>

      <div className="bg-white border border-zinc-200 rounded-xl shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-zinc-100 text-left">
            <th className="px-5 py-3 text-[10px] font-mono uppercase tracking-wider text-zinc-400">Name</th>
            <th className="px-5 py-3 text-[10px] font-mono uppercase tracking-wider text-zinc-400">E-Mail</th>
            <th className="px-5 py-3 text-[10px] font-mono uppercase tracking-wider text-zinc-400">Rolle</th>
            <th className="px-5 py-3 text-[10px] font-mono uppercase tracking-wider text-zinc-400">Status</th>
            <th className="px-5 py-3"></th>
          </tr></thead>
          <tbody className="divide-y divide-zinc-50">
            {users.map((u) => (
              <tr key={u.id} data-testid={`employee-row-${u.email}`} className="hover:bg-zinc-50">
                <td className="px-5 py-3">{u.first_name} {u.last_name}</td>
                <td className="px-5 py-3 text-zinc-500">{u.email}</td>
                <td className="px-5 py-3"><span className="text-[10px] font-mono bg-zinc-100 border border-zinc-200 px-2 py-0.5 rounded-full flex items-center gap-1 w-fit">{u.role === "admin" && <ShieldCheck className="w-3 h-3" />}{ROLES[u.role]?.split(" · ")[0] || u.role}</span></td>
                <td className="px-5 py-3">{u.active ? <span className="text-[10px] font-mono bg-zinc-900 text-white px-2 py-0.5 rounded-full">Aktiv</span> : <span className="text-[10px] font-mono bg-zinc-200 text-zinc-600 px-2 py-0.5 rounded-full">Inaktiv</span>}</td>
                <td className="px-5 py-3 text-right"><Button variant="outline" size="sm" onClick={() => toggleActive(u)} data-testid={`employee-toggle-${u.email}`}>{u.active ? "Deaktivieren" : "Aktivieren"}</Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="bg-white border border-zinc-200 rounded-xl p-6 shadow-sm">
        <h3 className="font-display font-medium mb-3">Rollen & Berechtigungen</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
          {Object.entries(ROLES).map(([k, v]) => (
            <div key={k} className="border border-zinc-100 rounded-lg p-3"><span className="font-mono text-xs text-zinc-400">{k}</span><div className="mt-1 text-zinc-700">{v.split(" · ")[1]}</div></div>
          ))}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="employee-form-dialog">
          <DialogHeader><DialogTitle>Mitarbeiter hinzufügen</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Vorname</Label><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="employee-firstname" className="mt-1.5" /></div>
              <div><Label>Nachname</Label><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="employee-lastname" className="mt-1.5" /></div>
            </div>
            <div><Label>E-Mail</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="employee-email" className="mt-1.5" /></div>
            <div><Label>Passwort (min. 8 Zeichen)</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="employee-password" className="mt-1.5" /></div>
            <div><Label>Rolle</Label>
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="employee-role" className="mt-1.5 w-full border border-zinc-200 rounded-lg px-3 py-2 text-sm bg-white">
                {Object.entries(ROLES).filter(([k]) => k !== "admin").map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                <option value="admin">{ROLES.admin}</option>
              </select>
            </div>
            <Button onClick={save} disabled={!form.email || form.password.length < 8} className="w-full bg-zinc-900 hover:bg-zinc-800 text-white" data-testid="employee-save-button">Anlegen</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
